import { useState, useEffect } from 'react';
import { 
  ChevronDown, ChevronRight, Building2, Users, UserCircle, 
  Ticket, Zap, Blocks, BarChart3, Database, Mail, X, LogOut, 
  LayoutDashboard, Key, Shield 
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import { ChangePasswordModal } from '@/components/auth/ChangePasswordModal';
import { useNavigate, useLocation } from 'react-router-dom';

type MenuItem = {
  label: string;
  route: string;
  requiredPermission?: string;
  roles?: string[];
};

type MenuSection = {
  id: string;
  title: string;
  icon: any;
  route?: string;
  items: MenuItem[];
  requiredPermission?: string;
  roles?: string[];
};

const MENU: MenuSection[] = [
  { 
    id: 'dashboard', 
    title: 'Dashboard', 
    icon: LayoutDashboard,
    route: '/admin/dashboard',
    requiredPermission: 'ticket.view',
    items: [
      { label: 'Overview', route: '/admin/dashboard', requiredPermission: 'ticket.view' },
      { label: 'Queue', route: '/admin/queue', requiredPermission: 'ticket.view' },
      { label: 'Audit Logs', route: '/admin/audit', requiredPermission: 'report.view' }
    ] 
  },
  { 
    id: 'soc', 
    title: 'SOC Use Cases', 
    icon: Shield, 
    requiredPermission: 'ticket.view',
    items: [
      { label: 'Use Case Pipeline (15 Steps)', route: '/admin/soc', requiredPermission: 'ticket.view' }
    ] 
  },
  { 
    id: 'org', 
    title: 'Organization', 
    icon: Building2, 
    requiredPermission: 'organization.view',
    items: [
      { label: 'Profile', route: '/admin/settings/profile', requiredPermission: 'organization.view' }, 
      { label: 'Departments', route: '/admin/settings/departments', requiredPermission: 'organization.view' }
    ] 
  },
  { 
    id: 'team', 
    title: 'Team Management', 
    icon: Users, 
    requiredPermission: 'team.view',
    items: [
      { label: 'Manage Teams', route: '/admin/teams', requiredPermission: 'team.view' }
    ] 
  },
  { 
    id: 'user', 
    title: 'User Management', 
    icon: UserCircle, 
    requiredPermission: 'user.view',
    items: [
      { label: 'Users', route: '/admin/users', requiredPermission: 'user.view' }, 
      { label: 'Roles', route: '/admin/users/roles', requiredPermission: 'user.update' }, 
      { label: 'Permissions', route: '/admin/users/permissions', requiredPermission: 'user.update' }
    ] 
  },
  { 
    id: 'ticket', 
    title: 'Ticket Configuration', 
    icon: Ticket, 
    requiredPermission: 'settings.manage',
    items: [
      { label: 'Categories', route: '/admin/tickets/categories', requiredPermission: 'settings.manage' }, 
      { label: 'Priorities', route: '/admin/tickets/priorities', requiredPermission: 'settings.manage' }, 
      { label: 'Workflow', route: '/admin/tickets/workflow', requiredPermission: 'settings.manage' }, 
      { label: 'Custom Fields', route: '/admin/tickets/custom-fields', requiredPermission: 'settings.manage' }
    ] 
  },
  { 
    id: 'automation', 
    title: 'Automation', 
    icon: Zap, 
    requiredPermission: 'automation.view',
    items: [
      { label: 'Auto Assignment', route: '/admin/automation/assignment', requiredPermission: 'automation.view' }, 
      { label: 'Escalation Rules', route: '/admin/automation/escalation', requiredPermission: 'automation.view' }, 
      { label: 'SLA Policies', route: '/admin/automation/sla', requiredPermission: 'automation.view' }, 
      { label: 'Approval Workflows', route: '/admin/automation/approvals', requiredPermission: 'automation.view' }
    ] 
  },
  { 
    id: 'email-config', 
    title: 'Email & Routing', 
    icon: Mail, 
    requiredPermission: 'integration.view',
    items: [
      { label: 'OAuth2 Settings', route: '/admin/email/smtp', requiredPermission: 'integration.view' }, 
      { label: 'Mailboxes', route: '/admin/email/mailboxes', requiredPermission: 'integration.view' },
      { label: 'Ticket Portal Configuration', route: '/admin/email/ticket-portal', requiredPermission: 'integration.view' }
    ] 
  },
  { 
    id: 'integrations', 
    title: 'Integrations', 
    icon: Blocks, 
    requiredPermission: 'integration.view',
    items: [
      { label: 'Manage Integrations', route: '/admin/integrations', requiredPermission: 'integration.view' },
      { label: 'SSO (Azure AD)', route: '/admin/sso-config', requiredPermission: 'integration.manage' },
      { label: 'Azure Sentinel Integration', route: '/admin/integrations/sentinel', roles: ['Super Admin', 'superadmin'], requiredPermission: 'integration.manage' }
    ] 
  },
  { 
    id: 'reports', 
    title: 'Reports & Analytics', 
    icon: BarChart3, 
    requiredPermission: 'report.view',
    items: [
      { label: 'Organization Reports', route: '/admin/reports', requiredPermission: 'report.view' }
    ] 
  },
  { 
    id: 'migration', 
    title: 'Data Migration', 
    icon: Database, 
    roles: ['Super Admin', 'superadmin'],
    requiredPermission: 'organization.delete',
    items: [
      { label: 'Import Manager', route: '/admin/migration' }
    ] 
  },
];

export function SettingsSlideDown({ open, onClose }: { open: boolean, onClose?: () => void }) {
  const [expanded, setExpanded] = useState<string[]>([]);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const { user, signOut, hasPermission, hasRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (open) {
      const activeIds = MENU.filter(s => s.items.some(i => location.pathname.includes(i.route))).map(s => s.id);
      setExpanded(prev => Array.from(new Set([...prev, ...activeIds])));
    }
  }, [open, location.pathname]);

  const toggleSection = (id: string) => {
    setExpanded((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const role = (user?.role || '').toLowerCase().trim();
  const email = (user?.email || '').toLowerCase().trim();
  const isSocUser = role.includes('soc') || 
    email.includes('achakkaravarthy') || 
    email.includes('ljayaraj') || 
    email.includes('hraj') || 
    email.includes('pramachandran');

  // Dynamically filter menu sections and items based on enterprise permissions & role
  const filteredMenu: MenuSection[] = isSocUser
    ? [
        {
          id: 'soc',
          title: 'SOC Use Case Management',
          icon: Shield,
          route: '/admin/soc',
          items: [
            { label: 'Use Case Pipeline (15 Steps)', route: '/admin/soc' },
            { label: 'Detection Lifecycle Dashboard', route: '/admin/soc' }
          ]
        }
      ]
    : MENU.map(section => {
        // Check section role constraint
        if (section.roles && !hasRole(section.roles)) {
          return null;
        }
        // Check section permission constraint
        if (section.requiredPermission && !hasPermission(section.requiredPermission)) {
          return null;
        }

        // Filter inner items
        const filteredItems = section.items.filter(item => {
          if (item.roles && !hasRole(item.roles)) {
            return false;
          }
          if (item.requiredPermission && !hasPermission(item.requiredPermission)) {
            return false;
          }
          return true;
        });

        return {
          ...section,
          items: filteredItems
        };
      }).filter((s): s is MenuSection => s !== null);

  const getDashboardRoute = () => {
    if (isSocUser) return '/admin/soc';
    if (role.includes('super admin') || role.includes('superadmin') || role === 'admin') return '/admin/dashboard';
    if (role === 'manager') return '/manager/dashboard';
    if (role === 'technician' || role.includes('technician') || role === 'agent' || role.includes('service desk')) return '/technician/dashboard';
    if (role === 'viewer') return '/viewer/dashboard';
    return '/user/dashboard';
  };

  const getQueueRoute = () => {
    if (isSocUser) return '/admin/soc';
    if (role.includes('super admin') || role.includes('superadmin') || role === 'admin') return '/admin/queue';
    if (role === 'manager') return '/manager/queue';
    if (role === 'technician' || role.includes('technician') || role === 'agent' || role.includes('service desk')) return '/technician/queue';
    return '/admin/queue';
  };

  return (
    <>
      <div 
        className={cn(
          "fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-all duration-300", 
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        )} 
        onClick={() => onClose?.()}
      />
      <div
        className={cn(
          'fixed left-0 top-0 bottom-0 z-50 w-80 bg-theme border-r border-[rgba(255,255,255,0.05)] shadow-2xl transition-transform duration-300 ease-out',
          open ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-full flex-col overflow-y-auto p-6 custom-scrollbar">
          <div className="flex items-center justify-between mb-8 px-2">
            <h2 className="font-display text-2xl font-bold tracking-tight text-theme">Menu</h2>
            <button onClick={() => onClose?.()} className="p-2 rounded-xl text-muted hover:bg-black/5 dark:hover:bg-white/5 transition-colors neo-interactive">
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <div className="space-y-3">
            {filteredMenu.map((section) => {
              const showExpanded = expanded.includes(section.id);
              const targetRoute = section.id === 'dashboard' ? getDashboardRoute() : section.route;

              return (
                <div key={section.id} className="mb-2">
                  <div
                    className={cn(
                      "flex w-full items-center justify-between rounded-xl text-sm font-bold transition-all",
                      showExpanded ? "text-[var(--color-primary)] neo" : "text-[var(--color-text)] hover:text-[var(--color-primary)] hover:bg-black/5 dark:hover:bg-white/5 opacity-80 hover:opacity-100"
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        if (targetRoute) {
                          navigate(targetRoute);
                          onClose?.();
                        } else {
                          toggleSection(section.id);
                        }
                      }}
                      className="flex items-center gap-3 flex-1 text-left px-4 py-3"
                    >
                      <section.icon className={cn("h-5 w-5", showExpanded ? "text-[var(--color-primary)]" : "text-[var(--color-text)]")} />
                      <span>{section.title}</span>
                    </button>
                    {section.items && section.items.length > 0 && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSection(section.id);
                        }}
                        className="px-3 py-3 text-muted-foreground hover:text-foreground transition-colors"
                        aria-label={`Toggle ${section.title} submenu`}
                      >
                        {showExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </button>
                    )}
                  </div>
                  <div
                    className={cn(
                      'overflow-hidden transition-all duration-300 ease-in-out',
                      showExpanded ? 'max-h-[500px] opacity-100 mt-2' : 'max-h-0 opacity-0'
                    )}
                  >
                    <div className="my-1 ml-6 space-y-1 border-l-2 border-[var(--color-primary)]/30 pl-3">
                      {section.items.map((item) => {
                        let itemRoute = item.route;
                        if (section.id === 'dashboard') {
                          if (item.label === 'Overview') itemRoute = getDashboardRoute();
                          else if (item.label === 'Queue') itemRoute = getQueueRoute();
                        }
                        const isActiveRoute = location.pathname === itemRoute;
                        return (
                          <button
                            key={item.label}
                            onClick={() => {
                              navigate(itemRoute);
                              onClose?.();
                            }}
                            className={cn(
                              "block w-full rounded-xl px-4 py-2.5 text-left text-sm font-medium transition-all hover:translate-x-1",
                              isActiveRoute 
                                ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)] opacity-100 font-bold"
                                : "text-[var(--color-text)] opacity-70 hover:bg-black/5 dark:hover:bg-white/5 hover:opacity-100 hover:text-[var(--color-primary)]"
                            )}
                          >
                            {item.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="mt-8 mb-4 border-t border-[rgba(255,255,255,0.05)] pt-6 px-1 space-y-2">
            <button
              type="button"
              onClick={() => setChangePasswordOpen(true)}
              className="flex w-full items-center justify-center gap-2.5 rounded-xl px-4 py-3 text-sm font-semibold text-foreground hover:bg-black/5 dark:hover:bg-white/5 transition-all neo-interactive"
            >
              <Key className="h-4 w-4 text-muted-foreground" />
              Change Password
            </button>
            <button
              type="button"
              onClick={async () => {
                await signOut();
              }}
              className="flex w-full items-center justify-center gap-3 rounded-xl px-4 py-3 text-sm font-bold text-red-500 bg-red-500/10 hover:bg-red-500 hover:text-white transition-all neo-interactive cursor-pointer"
            >
              <LogOut className="h-5 w-5" />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      <ChangePasswordModal 
        open={changePasswordOpen} 
        onClose={() => setChangePasswordOpen(false)} 
      />
    </>
  );
}
